import { toast } from 'react-toastify'
import KeycloakService from '../utils/keycloakService'
import {
  messages,
  endpoint,
  IpdPayload,
  HomekarePayload,
  LocationPayload,
  AmbulancePayload,
  SmartRingRemapPayload,
} from '../features/ProRithm/Constants'

export const addProrithmDevice = async (deviceId: string) => {
  const myHeaders = new Headers()
  myHeaders.append('Content-Type', 'application/json')
  myHeaders.append('Authorization', `Bearer ${KeycloakService.getToken()}`)

  const raw = JSON.stringify({
    deviceId: deviceId,
  })

  const requestOptions: RequestInit = {
    method: 'POST',
    headers: myHeaders,
    body: raw,
    redirect: 'follow',
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_GATEWAY_URL}${endpoint?.addDevice}`,
      requestOptions
    )

    if (!response?.ok) {
      const errorData = await response.json()
      const errorMessage = errorData?.message || messages?.failedToAddDevice
      throw new Error(errorMessage)
    }
    const result = await response.json()
    return result
  } catch (error: any) {
    console.error(error)
    toast(error?.message || messages?.failedToAddDevice, {
      position: 'bottom-center',
      hideProgressBar: true,
      closeButton: false,
      theme: 'dark',
      autoClose: 4000,
    })
  }
}

export const editProrithmMapping = async (
  mappingId: string | null | undefined,
  payload:
    | AmbulancePayload
    | HomekarePayload
    | IpdPayload
    | LocationPayload
    | SmartRingRemapPayload
) => {
  const myHeaders = new Headers()
  myHeaders.append('Content-Type', 'application/json')
  myHeaders.append('Authorization', `Bearer ${KeycloakService.getToken()}`)

  const raw = JSON.stringify(payload)
  const requestOptions: RequestInit = {
    method: 'PUT',
    headers: myHeaders,
    body: raw,
    redirect: 'follow',
  }

  const url = mappingId
    ? `${process.env.REACT_APP_API_GATEWAY_URL}${endpoint?.editProrithmMapping}/${mappingId}`
    : `${process.env.REACT_APP_API_GATEWAY_URL}${endpoint?.editProrithmMapping}`

  try {
    const response = await fetch(url, requestOptions)
    if (!response?.ok) {
      const errorData = await response?.json()
      const errorMessage = errorData?.message || messages?.failedToMapDevice
      throw new Error(errorMessage)
    }

    const result = await response?.json()
    return result
  } catch (error: any) {
    console.error(error)
    toast(error?.message || messages?.failedToMapDevice, {
      position: 'bottom-center',
      hideProgressBar: true,
      closeButton: false,
      theme: 'dark',
      autoClose: 4000,
    })
  }
}

export const getAllProrithmDevices = async (
  page: number,
  pageSize: number,
  deviceType?: string
) => {
  try {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${KeycloakService.getToken()}`)
    myHeaders.append('Content-Type', 'application/json')

    const requestOptions: RequestInit = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow',
    }

    const url = new URL(
      `${process.env.REACT_APP_API_GATEWAY_URL}${endpoint?.getAllDevices}`
    )
    url.searchParams.append('page', page.toString())
    url.searchParams.append('size', pageSize.toString())
    if (deviceType) {
      url.searchParams.append('deviceType', deviceType)
    }

    const response = await fetch(url.toString(), requestOptions)

    if (!response.ok) {
      const errorMessage = await response?.text()
      throw new Error(`Error: ${response.status} - ${errorMessage}`)
    }

    const result = await response?.json()
    return result ?? []
  } catch (error) {
    console.error(error)
    toast(messages?.getAllDevicesError, {
      position: 'bottom-center',
      hideProgressBar: true,
      closeButton: false,
      theme: 'dark',
      autoClose: 4000,
    })
  }
}

export const deleteProrithmDevice = async (deviceId: string) => {
  const myHeaders = new Headers()
  myHeaders.append('Authorization', `Bearer ${KeycloakService.getToken()}`)

  const requestOptions: RequestInit = {
    method: 'DELETE',
    headers: myHeaders,
    redirect: 'follow',
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_GATEWAY_URL}${endpoint?.deleteDevice}${deviceId}`,
      requestOptions
    )

    if (!response?.ok) {
      const errorData = await response?.json()
      const errorMessage = errorData?.message || messages?.deleteDeviceFailed
      throw new Error(errorMessage)
    }
    const result = await response?.json()
    return result
  } catch (error: any) {
    console.error(error)
    toast(error?.message || messages?.deleteDeviceFailed, {
      position: 'bottom-center',
      hideProgressBar: true,
      closeButton: false,
      theme: 'dark',
      autoClose: 4000,
    })
  }
}

export const searchPatient = async (
  type: string,
  value: string,
  isIpdCategory: boolean
) => {
  const token = KeycloakService.getToken()

  const myHeaders = new Headers()
  myHeaders.append('Authorization', `Bearer ${token}`)

  const requestOptions: RequestInit = {
    method: 'GET',
    headers: myHeaders,
    redirect: 'follow',
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_GATEWAY_URL}${endpoint?.searchPatient}type=${type}&value=${value}&isIpdCategory=${isIpdCategory}`,
      requestOptions
    )
    if (!response?.ok) throw new Error(messages?.searchPatientError)
    const data = await response?.json()
    return data
  } catch (error) {
    console.error(error)
    toast(messages?.searchPatientError, {
      position: 'bottom-center',
      hideProgressBar: true,
      closeButton: false,
      theme: 'dark',
      autoClose: 4000,
    })
  }
}

export const getBedsBasedOnLocation = async (location: string) => {
  const token = KeycloakService.getToken()
  const myHeaders = new Headers()
  myHeaders.append('Authorization', `Bearer ${token}`)

  const requestOptions: RequestInit = {
    method: 'GET',
    headers: myHeaders,
    redirect: 'follow',
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_GATEWAY_URL}${endpoint?.getBedsByLoc}${location}`,
      requestOptions
    )
    if (!response?.ok) throw new Error(messages?.bedDetailsError)
    const data = await response?.json()
    return data
  } catch (error) {
    console.error(error)
    toast(messages?.bedDetailsError, {
      position: 'bottom-center',
      hideProgressBar: true,
      closeButton: false,
      theme: 'dark',
      autoClose: 4000,
    })
  }
}

export const createUIDPatient = async (age: string, gender: string) => {
  const myHeaders = new Headers()
  myHeaders.append('Content-Type', 'application/json')
  myHeaders.append('Authorization', `Bearer ${KeycloakService.getToken()}`)

  const raw = JSON.stringify({
    age: age,
    gender: gender,
  })

  const requestOptions: RequestInit = {
    method: 'POST',
    headers: myHeaders,
    body: raw,
    redirect: 'follow',
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_GATEWAY_URL}${endpoint?.createUIDPatient}`,
      requestOptions
    )
    if (!response?.ok) {
      const errorData = await response.json()
      const errorMessage = errorData?.message || messages?.createUIDPatientError
      throw new Error(errorMessage)
    }
    const result = await response.json()
    return result
  } catch (error: any) {
    console.error(error)
    toast(error?.message || messages?.createUIDPatientError, {
      position: 'bottom-center',
      hideProgressBar: true,
      closeButton: false,
      theme: 'dark',
      autoClose: 4000,
    })
  }
}
